import { useContext, type CSSProperties, type JSX } from 'react';
import { SupernaturalContextValue } from './useSupernatural';
import type { SupernaturalRenderState } from './SupernaturalProvider';
import './atmosphere.css';

/*
 * SupernaturalOverlay.tsx — 全画面「気配（超常）」オーバーレイ（P2「端末は付喪神」基盤・2026-06-21）
 *
 * SupernaturalProvider の描画状態（active / intensity / type / connectionLabel）を購読し、
 * 全画面に “控えめな異界の滲み” を描く。
 *
 * 配置の前提（後続 Wave が決める）:
 *  - player-viewport 直下に置く前提。z-index は StatusHUD(90) より下・bg/コンテンツ(0〜5) より上の 30。
 *  - pointer-events: none を必ず付与（操作を絶対に妨げない）。
 *
 * 世界観ルール（§7.4）:
 *  - これは現象であって人格ではない。connection ラベル "……接続" は “状態表示の断片” であり台詞ではない。
 *  - 色を塗り替えず、無彩の暗い滲み + ごく薄いノイズに留める（atmosphere.css 側で担保）。
 */

/** intensity(0..1) → 既存 .sn-distortion--i{n} の刻みへ量子化（CSS と歩調を合わせる）。 */
function intensityClassSuffix(intensity: number): 3 | 5 | 7 | 10 {
  const i = Math.round(intensity * 10);
  if (i >= 9) return 10;
  if (i >= 6) return 7;
  if (i >= 4) return 5;
  return 3;
}

export function SupernaturalOverlay(): JSX.Element | null {
  // Provider は SupernaturalRenderState を value に入れている（公開型より広い）。
  // Overlay だけがこの拡張形を読む。Provider 外で誤って描画された場合は null（何も描かない）。
  const state = useContext(SupernaturalContextValue) as SupernaturalRenderState | null;

  if (!state || !state.active || state.intensity <= 0) {
    return null;
  }

  const { intensity, type, connectionLabel } = state;
  const iSuffix = intensityClassSuffix(intensity);

  // 全画面コンテナ。fixed・inset:0・pointer-events:none・HUD より下/bg より上の層。
  const containerStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: 30,
  };

  // 被膜の濃さ・filter 量は CSS 変数 1 つに集約して渡す。
  const veilStyle = {
    '--sn-veil-intensity': String(intensity),
  } as CSSProperties;

  const veilClass = `sn-fullscreen-veil sn-distortion--${type} sn-distortion--i${iSuffix}`;

  return (
    <div className="sn-atmosphere-overlay" style={containerStyle} aria-hidden="true">
      <div className={veilClass} style={veilStyle} />
      {connectionLabel !== null && (
        <div className="sn-connection-label">
          <span className="sn-connection-label__text">{connectionLabel}</span>
        </div>
      )}
    </div>
  );
}

export default SupernaturalOverlay;
